import axios from "axios";
import router from "./router.js";
import store from "@/store";

const instance = axios.create({
  baseURL: process.env.VUE_APP_API,
  withCredentials: true,
  timeout: 5000, // set the default timeout for requests to 5 seconds
});

instance.interceptors.request.use(
  (config) => {
    // Always get the most current token from localStorage on every request
    // This ensures we're always using the latest token after a refresh
    const authToken = localStorage.getItem("authToken");

    if (authToken) {
      config.headers["Authorization"] = `Bearer ${authToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.request.use(
  function (config) {
    const lang_id = store.getters["app/currentLanguage"];

    // List of URLs to exclude from language parameter addition
    const excludeUrls = [""];

    if (!excludeUrls.includes(config.url)) {
      if (config.method === "get") {
        config.params = {
          ...config.params,
        };
      } else if (config.data instanceof FormData) {
        return config;
      } else if (
        ["post", "put", "patch", "POST", "PUT", "PATCH"].includes(config.method)
      ) {
        config.data = {
          ...config.data,
          lang_id,
        };
      }
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const currentRoute = router.currentRoute.name; // Get the current route
    
    // Endpoints that should not trigger session timeout handling
    const ignoredUrls = [
      'is-auth', 
      'currencies-data',
      'faq',
      'static-pages',
      'social-links',
      'settings'
    ];
    
    // Session expiration detection and handling
    if (error.response && error.response.status === 401) {
      const currentUrl = error.config.url || '';
      const shouldIgnoreUrl = ignoredUrls.some(url => currentUrl.includes(url));
      
      if (shouldIgnoreUrl) {
        return Promise.reject(error);
      }
      
      const wasLoggedIn = localStorage.getItem("isAuthenticated") === "true";
      
      // Skip timeout modal for home page initial loads
      if (currentRoute === "Home" && !window.sessionStorage.getItem("wasActive")) {
        localStorage.setItem("isAuthenticated", "false");
        localStorage.setItem("emailVerified", "false");
        localStorage.removeItem("authToken");
        
        return Promise.reject(error);
      }
      
      // Prevent race conditions by checking recent user activity
      const lastActivity = window.sessionStorage.getItem('lastActivity');
      const now = Date.now();
      const recentActivity = lastActivity && (now - parseInt(lastActivity)) < 60000;
      
      // Session timeout criteria 
      const isRealTimeout = 
        wasLoggedIn &&
        (
          (error.response.data && error.response.data.reason === 'session_timeout') ||
          (error.config.url.includes('session/') && wasLoggedIn && !recentActivity) ||
          (window.sessionStorage.getItem("wasActive") === "true" && !recentActivity)
        );
      
      if (isRealTimeout) {
        console.log('Interceptor detected session timeout for URL:', error.config.url);
        
        try {
          window.sessionStorage.setItem("wasActive", "true");
          
          // Dispatch timeout event with detailed context for debugging
          const timeoutEvent = new CustomEvent('session-timeout', { 
            detail: { 
              url: error.config.url,
              reason: (error.response.data && error.response.data.reason) || 'auth_error_401',
              timestamp: new Date().toISOString(),
              lastActivity: lastActivity || 'unknown'
            },
            bubbles: true,
            cancelable: false
          });
          
          document.dispatchEvent(timeoutEvent);
          
          return Promise.reject(error);
        } catch (e) {
          console.error('Error dispatching session timeout event:', e);
        }
      } else if (wasLoggedIn) {
        // Handle race conditions with 401 errors during active usage
        if (recentActivity) {
          return Promise.reject(error);
        }
        
        localStorage.setItem("isAuthenticated", "false");
        localStorage.setItem("emailVerified", "false");
        localStorage.removeItem("authToken");
      }
      
      // Redirect for non-auth users trying to access protected routes
      const excludedRoutes = ["PasswordRestore", "BuyCrypto", "Home", "ConfirmationSuccess", "ConfirmationFailed", "TransactionConfirm"];
      
      if (!excludedRoutes.includes(currentRoute) && !wasLoggedIn) {
        if (router.currentRoute.name !== "Home") {
          router.push({ name: "Home" });
        }
      }
    }

    if (error.response && error.response.status === 403) {
      // Special case for locked accounts
      if (error.response.data && error.response.data.locked === true) {
        return Promise.reject(error);
      }
      
      if (currentRoute !== "PasswordRestore") {
        if (
          error.response.data.message ==
          "User identity not approved to submit orders"
        ) {
          router.push({ name: "Profile" });
        } else {
          localStorage.setItem("emailVerified", false.toString());
          if (router.currentRoute.name != "EmailVerificationNotice") {
            router.push({ name: "EmailVerificationNotice" });
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
